// ============================================================================
// PacketSuggestions — Phase 2B-2 (pending OCR prefill + section-level confirm)
//
// OCR candidates visually PREFILL the matching Patient/Admin form fields, but
// remain PENDING (unconfirmed) until the GP clicks Use (or "Use all" for a
// section). The field looks populated, but the system tracks per-field state:
//   pending   — OCR prefilled, awaiting confirmation (blocks Continue)
//   confirmed — GP Used (ACCEPT) or Used edited value (EDIT)
//   dismissed — GP Dismissed (REJECT); cleared only if still == OCR value
//
// Core rule: OCR candidate ≠ patient data. Prefill never becomes final PHI
// until the GP confirms (Use/Use-all) and submits. PHI is fetched only via the
// gp-only CF; review writes go to the candidate PHI doc; nothing here writes
// final referral PHI.
//
// PatientStepPHI passes a `fieldsRef` of accessors:
//   { get(fieldKey), setRaw(fieldKey, formVal), transform(fieldKey, value),
//     clear(fieldKey), isEmpty(fieldKey) }
// and an onPendingChange(count) callback (for the Continue blocker).
// ============================================================================

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Sparkles, Check, X, AlertCircle, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import {
  listPacketPatientAdminCandidates,
  reviewPacketPatientAdminCandidate,
  CANDIDATE_REVIEW_ACTION,
  CANDIDATE_ACTIONABLE_STATUSES,
} from '../../core/store.packets.js';

export const SECTION_FIELDS = Object.freeze({
  patient:   ['patient.name', 'patient.dateOfBirth', 'patient.sex', 'patient.identifier'],
  contact:   ['contact.phone', 'contact.email', 'contact.address'],
  insurance: ['insurance.payer', 'insurance.payerId', 'insurance.memberId', 'insurance.groupNumber', 'insurance.planType', 'insurance.planName'],
});
const SECTION_LABEL = { patient: 'patient', contact: 'contact', insurance: 'insurance' };

const NOT_APPLIED_FIELDS = new Set(['insurance.planName']); // captured, no form field

const STATUS_TEXT = {
  SUGGESTED:      'Suggested',
  LOW_CONFIDENCE: 'Needs confirmation',
  CONFLICTING:    'Conflict — choose one',
};

const applyValue   = (c) => (c.normalizedValue != null && c.normalizedValue !== '' ? c.normalizedValue : c.value) || '';
const displayValue = (c) => (c.reviewedValue != null ? c.reviewedValue : (c.normalizedValue != null ? c.normalizedValue : c.value)) || '';

// ── Consensus normalization (comparison only) ────────────────────────────────
// Core rule: duplicate agreement is NOT conflict — only DISTINCT normalized
// values create conflict. These normalizers are used ONLY to decide whether
// candidates agree; the value actually prefilled is still applyValue()+transform
// from the highest-confidence candidate (so display formatting is preserved).
const pad2 = (n) => String(n).padStart(2, '0');

// Date → yyyy-mm-dd for comparison (handles yyyy-m-d, mm/dd/yyyy, m-d-yyyy).
const normDob = (s) => {
  const v = String(s || '').trim();
  let m;
  if ((m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)))   return `${m[1]}-${pad2(m[2])}-${pad2(m[3])}`;
  if ((m = v.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/))) return `${m[3]}-${pad2(m[1])}-${pad2(m[2])}`;
  return v.toLowerCase();
};
const normSex = (s) => {
  const v = String(s || '').trim().toLowerCase();
  if (v.startsWith('m')) return 'male';
  if (v.startsWith('f')) return 'female';
  if (v.startsWith('u')) return 'unknown';
  return v;
};
const COMPARE_NORMALIZERS = {
  'patient.name':       (v) => v.toLowerCase().replace(/\s+/g, ' ').trim(),
  'patient.dateOfBirth': normDob,
  'patient.sex':        normSex,
  'patient.identifier': (v) => v.replace(/[\s-]/g, '').toLowerCase(),
  'contact.phone':      (v) => v.replace(/\D/g, ''),
  'contact.email':      (v) => v.toLowerCase().trim(),
  'contact.address':    (v) => v.toLowerCase().replace(/[.,]/g, ' ').replace(/\s+/g, ' ').trim(),
};
// Insurance + any other field → trim + collapse spaces + lowercase (safe).
const normalizeForCompare = (fieldKey, rawVal) => {
  const v = String(rawVal == null ? '' : rawVal).trim();
  if (!v) return '';
  const fn = COMPARE_NORMALIZERS[fieldKey];
  return fn ? fn(v) : v.toLowerCase().replace(/\s+/g, ' ').trim();
};

// Distinct-document count for a candidate group (provenance display).
const docCountOf = (group) => (group.docIds.size > 0 ? group.docIds.size : group.candidates.length);

// "Suggested from 7 documents" / "Suggested from Referral letter".
const provenanceText = (s) =>
  (s && s.docCount > 1)
    ? `Suggested from ${s.docCount} documents`
    : `Suggested from ${(s && s.documentLabel) || 'packet'}`;

const Ctx = createContext(null);

// ── Provider ─────────────────────────────────────────────────────────────────

// Build 2O — `deferUntil` (default true): the host can delay OCR prefill until
// the EHR (primary) source has filled patient/admin/insurance fields, so EHR wins
// the empty-field race. OCR's existing isEmpty guard then never overwrites an
// EHR-filled field; OCR conflicts/suggestions still surface for the GP.
export const PacketSuggestionsProvider = ({ packetId, fieldsRef, onPendingChange, deferUntil = true, children }) => {
  const [loading, setLoading] = useState(Boolean(packetId));
  const [error, setError]     = useState(null);
  const [fieldState, setFieldState] = useState({}); // fieldKey → { candidateId, baseline?, documentLabel, status, reviewStatus, noField?, value? }
  const [conflicts, setConflicts]   = useState({}); // fieldKey → [candidate]
  const [rowBusy, setRowBusy]       = useState({});  // fieldKey → bool
  const [sectionBusy, setSectionBusy] = useState({});// section → bool

  // Notify host of unresolved PENDING field count (drives the Continue blocker).
  // planName (noField) is excluded — it does not occupy a real field.
  useEffect(() => {
    const n = Object.values(fieldState).filter((s) => s.status === 'pending' && !s.noField).length;
    if (typeof onPendingChange === 'function') onPendingChange(n);
  }, [fieldState, onPendingChange]);

  const applyPrefill = useCallback((list) => {
    const f = fieldsRef && fieldsRef.current ? fieldsRef.current : null;
    const fs = {};
    const conf = {};

    // Group actionable candidates by field.
    const byField = {};
    for (const c of list) {
      if (CANDIDATE_ACTIONABLE_STATUSES.includes(c.status)) {
        (byField[c.fieldKey] = byField[c.fieldKey] || []).push(c);
      }
    }

    for (const fk of Object.keys(byField)) {
      const arr = byField[fk].slice().sort((a, b) => (b.confidence || 0) - (a.confidence || 0));

      // Collapse candidates that agree on the normalized value. arr is sorted by
      // confidence desc, so the FIRST candidate inserted into a group is its
      // highest-confidence representative. Duplicate agreement → one group.
      const groups = new Map(); // normKey → { key, rep, candidates[], docIds:Set, labels[] }
      for (const c of arr) {
        const key = normalizeForCompare(fk, applyValue(c));
        let g = groups.get(key);
        if (!g) { g = { key, rep: c, candidates: [], docIds: new Set(), labels: [] }; groups.set(key, g); }
        g.candidates.push(c);
        if (c.sourceDocumentId) g.docIds.add(c.sourceDocumentId);
        if (c.documentLabel && !g.labels.includes(c.documentLabel)) g.labels.push(c.documentLabel);
      }
      const groupList = Array.from(groups.values());

      if (NOT_APPLIED_FIELDS.has(fk)) {
        // No real form field — surface a single pending suggestion (top group).
        const g = groupList[0];
        fs[fk] = {
          candidateId: g.rep.candidateId,
          candidateIds: g.candidates.map((c) => c.candidateId),
          status: 'pending', noField: true,
          documentLabel: g.rep.documentLabel, docCount: docCountOf(g), documentLabels: g.labels,
          value: displayValue(g.rep), reviewStatus: g.rep.status,
        };
        continue;
      }
      if (!f) continue;

      if (groupList.length > 1) {
        // Real conflict — DISTINCT normalized values. Dedupe to one option per
        // unique value (NOT one row per document), each with provenance.
        conf[fk] = groupList.map((g) => ({
          key: g.key,
          value: displayValue(g.rep),
          applyVal: applyValue(g.rep),
          candidateIds: g.candidates.map((c) => c.candidateId),
          docCount: docCountOf(g),
          documentLabels: g.labels,
          rep: g.rep,
        }));
        continue;
      }

      // Consensus — all candidates agree. Prefill once; one pending suggestion.
      const g = groupList[0];
      if (!f.isEmpty(fk)) continue;                          // manual value present → manual wins
      const formVal = f.transform(fk, applyValue(g.rep));
      f.setRaw(fk, formVal);                                 // visually prefill the real field
      fs[fk] = {
        candidateId: g.rep.candidateId,
        candidateIds: g.candidates.map((c) => c.candidateId),
        status: 'pending', baseline: formVal,
        documentLabel: g.rep.documentLabel, docCount: docCountOf(g), documentLabels: g.labels,
        reviewStatus: g.rep.status,
      };
    }

    // Already-reviewed candidates from a prior session → show confirmed (no prefill).
    for (const c of list) {
      if (fs[c.fieldKey] || conf[c.fieldKey]) continue;
      if (c.status === 'ACCEPTED' || c.status === 'EDITED') {
        fs[c.fieldKey] = { candidateId: c.candidateId, status: 'confirmed', documentLabel: c.documentLabel, reviewStatus: c.status };
      }
      // REJECTED → ignore (hidden)
    }

    setFieldState(fs);
    setConflicts(conf);
  }, [fieldsRef]);

  const load = useCallback(() => {
    if (!packetId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    listPacketPatientAdminCandidates(packetId)
      .then((res) => applyPrefill(Array.isArray(res?.candidates) ? res.candidates : []))
      .catch((err) => {
        console.error('[PacketSuggestions] list failed:', err?.message);
        setError('Could not load packet suggestions. You can continue manually.');
      })
      .finally(() => setLoading(false));
  }, [packetId, applyPrefill]);

  useEffect(() => { if (deferUntil) load(); }, [load, deferUntil]);

  const callReview = async (candidateId, action, editedValue) => {
    try {
      const res = await reviewPacketPatientAdminCandidate({ packetId, candidateId, action, editedValue });
      return res?.status || action;
    } catch (err) {
      console.error('[PacketSuggestions] review failed:', err?.message);
      throw err;
    }
  };

  const setStatus = (fk, status, reviewStatus) =>
    setFieldState((prev) => ({ ...prev, [fk]: { ...prev[fk], status, reviewStatus } }));

  const candidateIdsOf = (s) => (Array.isArray(s.candidateIds) && s.candidateIds.length ? s.candidateIds : [s.candidateId]);

  // Confirm one pending field (possibly backed by several consensus candidates).
  // Unchanged from OCR → ACCEPT all underlying candidates. Edited → mark the
  // representative candidate EDITED with the GP's value; the rest ACCEPTED
  // (they agreed with the consensus the GP refined).
  const confirmOne = async (fk, s) => {
    const f = fieldsRef && fieldsRef.current ? fieldsRef.current : null;
    let editedVal;
    if (!s.noField && f) {
      const cur = f.get(fk);
      if (cur !== s.baseline) editedVal = cur;
    }
    const ids = candidateIdsOf(s);
    for (const id of ids) {
      if (editedVal !== undefined && id === s.candidateId) {
        await callReview(id, CANDIDATE_REVIEW_ACTION.EDIT, editedVal);
      } else {
        await callReview(id, CANDIDATE_REVIEW_ACTION.ACCEPT);
      }
    }
    setStatus(fk, 'confirmed', editedVal !== undefined ? 'EDITED' : 'ACCEPTED');
  };

  // Dismiss one pending field: REJECT every underlying candidate; clear the
  // field only if it is still unchanged from the OCR baseline (manual wins).
  const dismissOne = async (fk, s) => {
    const f = fieldsRef && fieldsRef.current ? fieldsRef.current : null;
    for (const id of candidateIdsOf(s)) {
      await callReview(id, CANDIDATE_REVIEW_ACTION.REJECT);
    }
    if (!s.noField && f && f.get(fk) === s.baseline) f.clear(fk);
    setStatus(fk, 'dismissed', 'REJECTED');
  };

  const useField = useCallback(async (fk) => {
    const s = fieldState[fk];
    if (!s || s.status !== 'pending') return;
    setRowBusy((b) => ({ ...b, [fk]: true }));
    try { await confirmOne(fk, s); } catch { /* keep pending; error surfaced elsewhere */ }
    finally { setRowBusy((b) => ({ ...b, [fk]: false })); }
  }, [fieldState]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissField = useCallback(async (fk) => {
    const s = fieldState[fk];
    if (!s || s.status !== 'pending') return;
    setRowBusy((b) => ({ ...b, [fk]: true }));
    try { await dismissOne(fk, s); } catch { /* keep pending */ }
    finally { setRowBusy((b) => ({ ...b, [fk]: false })); }
  }, [fieldState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Conflict resolution: choosing one unique-value OPTION prefills + ACCEPTs all
  // of that value's candidates, and REJECTs every other option's candidates so
  // nothing is left unresolved for the field.
  const useConflictChoice = useCallback(async (fk, option) => {
    const f = fieldsRef && fieldsRef.current ? fieldsRef.current : null;
    setRowBusy((b) => ({ ...b, [fk]: true }));
    try {
      let baseline = null;
      if (f && !NOT_APPLIED_FIELDS.has(fk)) { baseline = f.transform(fk, option.applyVal); f.setRaw(fk, baseline); }
      for (const id of option.candidateIds) await callReview(id, CANDIDATE_REVIEW_ACTION.ACCEPT);
      const others = (conflicts[fk] || []).filter((o) => o.key !== option.key);
      for (const o of others) { for (const id of o.candidateIds) await callReview(id, CANDIDATE_REVIEW_ACTION.REJECT); }
      setConflicts((prev) => { const n = { ...prev }; delete n[fk]; return n; });
      setFieldState((prev) => ({ ...prev, [fk]: { candidateId: option.rep.candidateId, candidateIds: option.candidateIds, status: 'confirmed', documentLabel: option.rep.documentLabel, docCount: option.docCount, reviewStatus: 'ACCEPTED', baseline } }));
    } catch { /* keep conflict */ }
    finally { setRowBusy((b) => ({ ...b, [fk]: false })); }
  }, [fieldsRef, conflicts]);

  // Section bulk — snapshot pending fields, then process sequentially.
  const useSection = useCallback(async (section) => {
    const fks = (SECTION_FIELDS[section] || []).filter((fk) => fieldState[fk] && fieldState[fk].status === 'pending');
    if (fks.length === 0) return;
    setSectionBusy((b) => ({ ...b, [section]: true }));
    try { for (const fk of fks) { try { await confirmOne(fk, fieldState[fk]); } catch { /* skip */ } } }
    finally { setSectionBusy((b) => ({ ...b, [section]: false })); }
  }, [fieldState]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissSection = useCallback(async (section) => {
    const fks = (SECTION_FIELDS[section] || []).filter((fk) => fieldState[fk] && fieldState[fk].status === 'pending');
    if (fks.length === 0) return;
    setSectionBusy((b) => ({ ...b, [section]: true }));
    try { for (const fk of fks) { try { await dismissOne(fk, fieldState[fk]); } catch { /* skip */ } } }
    finally { setSectionBusy((b) => ({ ...b, [section]: false })); }
  }, [fieldState]); // eslint-disable-line react-hooks/exhaustive-deps

  const sectionHasPending = useCallback((section) =>
    (SECTION_FIELDS[section] || []).some((fk) => fieldState[fk] && fieldState[fk].status === 'pending'),
  [fieldState]);

  const getFieldValue = useCallback((fk) => {
    const f = fieldsRef && fieldsRef.current ? fieldsRef.current : null;
    return f ? f.get(fk) : '';
  }, [fieldsRef]);

  const value = {
    packetId, loading, error, fieldState, conflicts, rowBusy, sectionBusy,
    useField, dismissField, useConflictChoice, useSection, dismissSection,
    sectionHasPending, getFieldValue,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

// ── Header note ──────────────────────────────────────────────────────────────

export const PacketSuggestionsNote = () => {
  const ctx = useContext(Ctx);
  if (!ctx || !ctx.packetId) return null;
  if (ctx.loading) {
    return <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading packet suggestions…</div>;
  }
  if (ctx.error) {
    return <div className="flex items-start gap-2 p-2.5 rounded-lg border border-amber-700/40 bg-amber-900/20 text-xs text-amber-200"><AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {ctx.error}</div>;
  }
  return <div className="flex items-center gap-2 text-xs text-indigo-300"><Sparkles className="w-3.5 h-3.5" /> Suggestions prefill the fields below but are not patient data until you Use them.</div>;
};

// ── Section-level Use all / Dismiss all ──────────────────────────────────────

export const SectionSuggestionControls = ({ section }) => {
  const ctx = useContext(Ctx);
  if (!ctx || !ctx.packetId) return null;
  if (!ctx.sectionHasPending(section)) return null;
  const busy = ctx.sectionBusy[section];
  return (
    <div className="mb-2 flex items-center gap-2 flex-wrap">
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-600/80 text-amber-50">
        <Sparkles className="w-3 h-3" /> Pending OCR — needs confirmation
      </span>
      <button type="button" onClick={() => ctx.useSection(section)} disabled={busy}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Use all {SECTION_LABEL[section]} suggestions
      </button>
      <button type="button" onClick={() => ctx.dismissSection(section)} disabled={busy}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded border border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-50">
        <X className="w-3.5 h-3.5" /> Dismiss all
      </button>
    </div>
  );
};

// ── Inline compact confirmation row under each field ─────────────────────────

// conflictOnly: render ONLY a compact conflict row (used by the Insurance
// section, which resolves non-conflicting suggestions via section-level
// "Use all / Dismiss all" instead of per-field panels).
export const FieldSuggestions = ({ fieldKey, conflictOnly = false }) => {
  const ctx = useContext(Ctx);
  if (!ctx || !ctx.packetId) return null;

  const conflict = ctx.conflicts[fieldKey];
  const s = ctx.fieldState[fieldKey];
  const busy = ctx.rowBusy[fieldKey];

  // Conflict: do not prefill; show ONE option per unique normalized value (not
  // one row per document). Each option shows how many documents support it.
  if (conflict && conflict.length > 0) {
    return (
      <div className="mt-1.5 rounded-md border border-orange-700/40 bg-orange-950/20 p-2 text-xs">
        <div className="flex items-center gap-1.5 text-orange-200"><Sparkles className="w-3 h-3" /> Conflict — choose one</div>
        <div className="mt-1 space-y-1.5">
          {conflict.map((opt) => (
            <div key={opt.key} className="flex items-center gap-2">
              <span className="flex-1 text-slate-100 break-words">{opt.value}</span>
              <span className="text-[10px] text-slate-500">
                found in {opt.docCount} document{opt.docCount === 1 ? '' : 's'}
              </span>
              <button type="button" onClick={() => ctx.useConflictChoice(fieldKey, opt)} disabled={busy}
                className="px-2 py-0.5 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">Use</button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // conflictOnly mode (Patient/Contact/Insurance sections): pending/confirmed are
  // driven by the section-level Use all / Dismiss all controls. Render only a
  // COMPACT one-line status here — never a giant duplicate panel.
  if (conflictOnly) {
    if (s && s.status === 'pending') {
      return (
        <div className="mt-1 text-[11px] text-indigo-300 flex items-center gap-1">
          <Sparkles className="w-3 h-3 shrink-0" /> {provenanceText(s)} · needs confirmation
        </div>
      );
    }
    if (s && s.status === 'confirmed') {
      const editedNote = s.reviewStatus === 'EDITED' ? 'Used edited value' : 'Used';
      return (
        <div className="mt-1 text-[11px] text-emerald-400 flex items-center gap-1">
          <Check className="w-3 h-3 shrink-0" /> {editedNote}{s.docCount > 1 ? ` · ${s.docCount} documents` : ''}
        </div>
      );
    }
    return null;
  }

  if (!s) return null;
  if (s.status === 'dismissed') return null; // hidden

  if (s.status === 'confirmed') {
    const editedNote = s.reviewStatus === 'EDITED' ? 'Used edited value' : 'Used';
    return (
      <div className="mt-1 text-[11px] text-emerald-400 flex items-center gap-1">
        <Check className="w-3 h-3" /> {editedNote} from {s.documentLabel || 'packet'}
      </div>
    );
  }

  // pending
  const needsConfirm = s.reviewStatus === 'LOW_CONFIDENCE';
  const edited = !s.noField && ctx.getFieldValue(fieldKey) !== s.baseline;
  const useLabel = edited ? 'Use edited value' : 'Use';
  return (
    <div className="mt-1.5 rounded-md border border-indigo-700/40 bg-indigo-950/30 p-2 text-xs">
      <div className="flex items-center gap-1.5 flex-wrap">
        <Sparkles className="w-3 h-3 text-indigo-300 shrink-0" />
        <span className="text-indigo-200">{provenanceText(s)}</span>
        <span className={'px-1.5 py-0.5 rounded text-[10px] font-medium ' + (needsConfirm ? 'bg-amber-600 text-amber-50' : 'bg-slate-600 text-slate-100')}>
          {needsConfirm ? STATUS_TEXT.LOW_CONFIDENCE : 'Needs confirmation'}
        </span>
      </div>
      {s.noField && <div className="mt-0.5 text-slate-300">{s.value}<span className="ml-1 text-[11px] text-slate-500">· not applied to this form</span></div>}
      <div className="mt-1.5 flex items-center gap-1.5">
        <button type="button" onClick={() => ctx.useField(fieldKey)} disabled={busy}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {useLabel}
        </button>
        <button type="button" onClick={() => ctx.dismissField(fieldKey)} disabled={busy}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-50">
          <X className="w-3.5 h-3.5" /> Dismiss
        </button>
      </div>
    </div>
  );
};

// ── Insurance payer warning (pending-aware) ──────────────────────────────────

export const PacketPayerWarning = ({ needsPayer }) => {
  const ctx = useContext(Ctx);
  const payerPending = ctx && ctx.fieldState && ctx.fieldState['insurance.payer'] && ctx.fieldState['insurance.payer'].status === 'pending';
  if (payerPending) {
    return (
      <div className="flex items-center gap-2 p-2.5 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs text-amber-300">
        <AlertCircle className="w-4 h-4 flex-shrink-0" /> Use payer name to save insurance details.
      </div>
    );
  }
  if (needsPayer) {
    return (
      <div className="flex items-center gap-2 p-2.5 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs text-amber-300">
        <AlertCircle className="w-4 h-4 flex-shrink-0" /> Add payer name to save insurance details.
      </div>
    );
  }
  return null;
};

export default FieldSuggestions;
